const CONTAINER_PREFIX = 'GTM-';
const GOOGLE_TAG_PREFIXES = ['G-', 'AW-', 'GT-', 'DC-'];

const GTAG_LOADER_URL = 'https://www.googletagmanager.com/gtag/js?id=';
const GTM_LOADER_URL = 'https://www.googletagmanager.com/gtm.js?id=';
const GTM_NOSCRIPT_URL = 'https://www.googletagmanager.com/ns.html?id=';

const normalizeId = (id) => (typeof id === 'string' ? id.trim() : '');

const insertScriptToHead = (src) => {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.insertBefore(script, document.head.firstChild);
};

/**
 * Container GTM sejati (gtm.js). Hanya prefix GTM- yang boleh lewat jalur ini.
 * @param {string} id
 * @returns {boolean}
 */
export const isContainerId = (id) => normalizeId(id).toUpperCase().indexOf(CONTAINER_PREFIX) === 0;

/**
 * Google tag (GA4 / Ads / Floodlight) yang wajib dimuat lewat gtag/js.
 * @param {string} id
 * @returns {boolean}
 */
export const isGoogleTagId = (id) => {
  const upperId = normalizeId(id).toUpperCase();
  if (!upperId || isContainerId(upperId)) return false;
  return GOOGLE_TAG_PREFIXES.some((prefix) => upperId.indexOf(prefix) === 0);
};

/**
 * Memilah daftar ID merchant ke jalur pemuatan yang benar berdasarkan prefix,
 * bukan berdasarkan nama field — field google_tag_manager_id kerap diisi
 * measurement ID (G-...), dan Google melarang men-deploy Google tag lewat gtm.js.
 * @param {Array<string>} ids
 * @returns {{ containerIds: Array<string>, googleTagIds: Array<string> }}
 */
export const classifyTagIds = (ids = []) => {
  const containerIds = [];
  const googleTagIds = [];

  ids.forEach((rawId) => {
    const id = normalizeId(rawId);
    if (!id) return;

    if (isContainerId(id)) {
      if (containerIds.indexOf(id) === -1) containerIds.push(id);
      return;
    }

    if (isGoogleTagId(id) && googleTagIds.indexOf(id) === -1) googleTagIds.push(id);
  });

  return { containerIds, googleTagIds };
};

/**
 * Stub gtag kanonik. Cukup menulis ke dataLayer sehingga event yang terjadi
 * sebelum loader selesai diunduh tetap ter-antri, bukan hilang.
 */
export const ensureGtagStub = () => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === 'function') return;

  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
};

/**
 * Memuat snippet gtag/js sekali, lalu men-config setiap Google tag ID.
 * @param {Array<string>} googleTagIds
 */
export const loadGoogleTag = (googleTagIds = []) => {
  if (!googleTagIds.length) return;
  ensureGtagStub();

  if (!window.isGoogleTagLoaded) {
    insertScriptToHead(GTAG_LOADER_URL + encodeURIComponent(googleTagIds[0]));
    window.gtag('js', new Date());
    window.isGoogleTagLoaded = true;
  }

  window.configuredGoogleTagIds = window.configuredGoogleTagIds || [];
  googleTagIds.forEach((id) => {
    if (window.configuredGoogleTagIds.indexOf(id) !== -1) return;

    // page_view dikirim manual per route (router.afterEach), jadi config tidak boleh ikut mengirim.
    window.gtag('config', id, { send_page_view: false });
    window.configuredGoogleTagIds.push(id);
  });
};

/**
 * Memuat container GTM beserta fallback noscript-nya, idempoten per container.
 * @param {string} containerId
 */
export const loadTagManager = (containerId) => {
  const id = normalizeId(containerId);
  if (!isContainerId(id)) return;

  ensureGtagStub();

  window.injectedGtmContainers = window.injectedGtmContainers || [];
  if (window.injectedGtmContainers.indexOf(id) !== -1) return;
  window.injectedGtmContainers.push(id);

  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  insertScriptToHead(GTM_LOADER_URL + encodeURIComponent(id));

  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = GTM_NOSCRIPT_URL + encodeURIComponent(id);
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
};



// WEBPACK FOOTER //
// ./src/utils/googleTag.js